import { BLUE_600, CORAL_100, CORAL_600, GRAY_100, GRAY_400, GRAY_800, GRAY_900, WHITE } from 'constants/styling/theme'
import { InputAdornment, InputBase, Stack, Typography, inputBaseClasses, styled } from '@mui/material'
import React, { ChangeEvent, KeyboardEvent, ReactNode, useMemo } from 'react'

import { BorderBoxWrapper } from '../BorderBoxWrapper/BorderBoxWrapper.component'
import { CopyButton } from '../CopyButton'
import { KeyboardEventKey } from 'constants/misc'
import { Label } from '../Label'
import { MUIInputFieldButton } from './MUIInputFieldButton'

export enum InputFieldStyleTypes {
  DEFAULT,
  COPY
}

/**
 * @typedef InputStyleType Available input types
 */
export type InputStyleType = InputFieldStyleTypes.DEFAULT | InputFieldStyleTypes.COPY

/**
 * @interface MUIInputFieldProps Input properties
 */
export interface MUIInputFieldProps {
  /** Input type style */
  type?: InputStyleType,
  /** Input value */
  value?: string,
  /** Input placeholder */
  placeholder?: string,
  /** Custom input box width */
  inputBoxWidth?: string,
  /** Custom input box height */
  inputBoxHeight?: string,
  /** Whether the input is read only or not */
  readOnly?: boolean,
  /** Input block label */
  label?: string
  /** Whether input is error or not */
  isError?: boolean
  /** Input error text */
  errorText?: string | null
  /** Input hint text */
  hintText?: string | null | ReactNode
  /** Allow input field with multiple lines. Material UI will automatically treat input as textarea. */
  isMultiline?: boolean
  /** Whether the input is disabled or not */
  disabled?: boolean
  /** Whether the input is required or not */
  required?: boolean
  /** Custom input component */
  customInput?: ReactNode
  /** Custom suffix to be displayed at the end of the input. */
  suffix?: string
  /** Custom prefix to be displayed at the start of the input. */
  prefix?: string
  /** Native input type (`date`, `time`, `number`, …), default is 'text'. Ignored by a multiline field */
  inputElementType?: string,
  /** Bounds and step forwarded to the native input, e.g. `{ min: '09:00', step: 900 }` for a quarter-hour time field */
  inputAttributes?: { min?: string | number, max?: string | number, step?: number }
  /** Rows a multiline field starts with (it still grows with its content). Only with `isMultiline` */
  rows?: number
  /** Custom tooltip besides label */
  tooltip?: ReactNode,
  /** Optional icon in the start position */
  startIcon?: ReactNode
  /** Optional icon in the end position */
  endIcon?: ReactNode
  /** Optional text length limitation (Automatically truncates out-coming event text) */
  maxLength?: number
  /** When true, masks the input contents from sessions recording. Use for PII inputs. */
  sensitive?: boolean
  /** Browser autofill behaviour, forwarded to the native input. */
  autoComplete?: string
  /** ARIA attributes merged onto the NATIVE input — a combobox's listbox wiring
   *  (`role`, `aria-expanded`, `aria-controls`, `aria-activedescendant`) has to
   *  land there rather than on the MUI wrapper to be announced. */
  inputAriaProps?: React.AriaAttributes & { role?: string }
  /** OnChange action to update the input value */
  onBlur?: (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void
  /** OnFocus action, e.g. to open a results dropdown */
  onFocus?: (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void
  /** OnChange action to update the input value */
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void
  /** OnClick action to copy the input value */
  onClickCopy?: () => void
  /** onKeyDown action triggered when user presses any key on the keyboard */
  onKeyDown?: (e: KeyboardEvent) => void
}

export const StyledMUIInput = styled(InputBase)(({ error, required, startAdornment, endAdornment }) => {
  return {
    width: '100%',
    flex: '1 1 6rem',
    backgroundColor: WHITE,
    borderRadius: '.8rem',
    [`.${inputBaseClasses.input}`]: {
      padding: `.8rem ${!startAdornment || !endAdornment ? '1.2rem' : '0rem'} !important`,
      border: 'unset !important',
      borderRadius: '.8rem',
      color: `${error ? CORAL_600 : GRAY_900} !important`,
      boxShadow: 'unset !important',
      ':focus': {
        color: `${GRAY_900} !important`
      }
    },
    [`.${inputBaseClasses.input}:disabled`]: {
      backgroundColor: `${GRAY_100} !important`,
    },
    // MUI sizes a multiline field as `rows` × this hidden twin's height: the forced padding above, or the legacy
    // `textarea { min-height }`, would inflate every row (7 rows came out ~252px instead of ~156px)
    [`.${inputBaseClasses.input}[aria-hidden="true"]`]: {
      paddingTop: '0 !important',
      paddingBottom: '0 !important',
      minHeight: '0 !important',
    },
  }
})

/**
 * @component MUI Input field
 * @example
 * <MUIInputField
 *  type="copy"
 *  value="owo!"
 *  onChange={() => {}}
 * />
 */
export const MUIInputField: React.FC<MUIInputFieldProps> = React.forwardRef(({
  type = 'default',
  value,
  isError,
  errorText,
  hintText,
  placeholder,
  inputBoxWidth,
  inputBoxHeight,
  readOnly = false,
  label = undefined,
  isMultiline,
  disabled = false,
  required,
  customInput,
  suffix,
  prefix,
  inputElementType,
  inputAttributes,
  rows,
  startIcon,
  endIcon,
  maxLength,
  tooltip,
  sensitive,
  onBlur,
  onChange,
  onClickCopy,
  onKeyDown,
  autoComplete,
  inputAriaProps,
  onFocus,
}, ref) => {

  const hoverBorderColor = useMemo(() => {
    if (isError) return 'inherit'
    if (disabled) return GRAY_400
    return BLUE_600
  }, [disabled, isError])

  // A multiline field with a set height fills it from the top and scrolls, instead of centring a textarea
  // the legacy stylesheet (`html[bkbn-style="old"] textarea { min-height: 10rem }`) can make taller than the box.
  const fillsFixedBox = !!isMultiline && !!inputBoxHeight

  const inputBgColor = useMemo(() => {
    if (isError) return CORAL_100
    if (disabled) return GRAY_100
    return 'inherit'
  }, [disabled, isError])

  return (
    <Stack gap={.6} className={sensitive ? 'fs-mask' : undefined}>
      {!!label &&
        <Label
          text={label}
          tooltip={tooltip}
          isError={isError}
          required={required}
        />
      }

      <BorderBoxWrapper
        borderColor={isError ? CORAL_600 : GRAY_400}
        backgroundColor={inputBgColor}
        component="form"
        sx={{
          display: 'flex',
          alignItems: fillsFixedBox ? 'stretch' : 'center',
          justifyContent: 'space-between',
          transition: '.3s ease',
          boxSizing: 'initial',
          borderRadius: '.8rem',
          overflow: 'hidden',
          width: inputBoxWidth,
          height: inputBoxHeight || (isMultiline ? undefined : '4rem'),
          '&:hover': {
            border: `.1rem solid ${hoverBorderColor}`,
          }
        }}
      >

        {prefix &&
          <MUIInputFieldButton borderType="right" disabled>
            <Typography variant="text-md" color={GRAY_900} fontWeight={500}>
              {prefix}
            </Typography>
          </MUIInputFieldButton>
        }

        {customInput ||
          <StyledMUIInput
            value={value}
            error={isError}
            disabled={disabled}
            ref={ref}
            placeholder={placeholder}
            readOnly={readOnly}
            onBlur={(e) => onBlur?.(e)}
            onFocus={(e) => onFocus?.(e)}
            onChange={(e) => onChange?.(e as ChangeEvent<HTMLInputElement>)}
            type={inputElementType}
            // `rows` goes through inputProps: InputBase's own `rows` prop would also cap the height at that count
            inputProps={{ maxLength, autoComplete, ...(isMultiline && rows ? { rows } : {}), ...inputAttributes, ...inputAriaProps }}
            multiline={isMultiline}
            minRows={isMultiline ? rows : undefined}
            onKeyDown={(e) => {
              if (e.key === KeyboardEventKey.ENTER && !isMultiline) e.preventDefault()
              onKeyDown?.(e)
            }}
            required={required}
            startAdornment={
              startIcon ? (
                <InputAdornment
                  position="start"
                  sx={{
                    paddingLeft: '1.2rem',
                    color: GRAY_800,
                  }}
                >
                  {startIcon}
                </InputAdornment>
              ) : undefined
            }
            endAdornment={
              endIcon ? (
                <InputAdornment
                  position="end"
                  sx={{
                    paddingRight: '1.2rem',
                    color: GRAY_800,
                  }}
                >
                  {endIcon}
                </InputAdornment>
              ) : undefined
            }
            sx={{
              'input[readonly]': {
                cursor: 'not-allowed',
              },
              ...(fillsFixedBox && {
                alignItems: 'flex-start',
                'html[bkbn-style="old"] && textarea, && textarea': {
                  height: '100% !important',
                  minHeight: '0 !important',
                  boxSizing: 'border-box !important',
                  overflow: 'auto !important',
                },
              }),
            }}
          />
        }

        {type === InputFieldStyleTypes.COPY &&
          <MUIInputFieldButton component="div">
            <CopyButton
              variant='text'
              showIcon={true}
              showTooltip={false}
              showIdleIcon={true}
              onCopy={onClickCopy}
            />
          </MUIInputFieldButton>
        }

        {suffix &&
          <MUIInputFieldButton disabled>
            <Typography variant="text-md" color={GRAY_900} fontWeight={500}>
              {suffix}
            </Typography>
          </MUIInputFieldButton>
        }

      </BorderBoxWrapper>

      {!!hintText && !isError &&
        <Label text={hintText} />
      }

      {isError && errorText &&
        <Label text={errorText} isError={true} />
      }

    </Stack>
  )
})
